import {PageViewModel} from '@components/ui/table-template/models/page-view-model';
import {FilterService} from '@shared/services/filter.service';
import {inject, Injectable} from '@angular/core';
import {FormattedNumber} from '@components/ui/table-template/classes/formatted-number';
import {IconViewModel} from '@components/ui/table-template/models/icon-view-model';
import {Keys} from '@shared/keys';

@Injectable()
export class ClientsideDataService<T> {

  private readonly collator: Intl.Collator;

  private data: T[];

  private lastSortedField: string;
  private lastSortedAscending: boolean;
  private sortedData: T[];

  private lastSearchFields: Keys<T>;
  private lastSearchTerm: string;
  private filteredData: T[];

  private readonly lastPageNumber: number;
  private readonly lastPageSize: number;
  private pagedData: T[];

  private readonly filterService = inject(FilterService);

  constructor() {
    const collatorOptions: Intl.CollatorOptions = {
      sensitivity: 'base',
      numeric: true
    };
    this.collator = new Intl.Collator(undefined, collatorOptions);
  }

  initialize(initialData: T[]): this {
    this.data = initialData;
    return this;
  }

  generatePage(sortField: string,
               sortAscending: boolean,
               searchFields: Keys<T>,
               searchTerm: string,
               pageNumber: number,
               pageSize: number): PageViewModel<T> {

    let cacheInvalidated = false;

    if (sortField !== this.lastSortedField || sortAscending !== this.lastSortedAscending) {
      cacheInvalidated = true;

      this.lastSortedField = sortField;
      this.lastSortedAscending = sortAscending;
      this.sortedData = this.applySort(this.data, sortField, sortAscending);
    }

    if (searchFields) {
      searchFields.sort((a, b) => String(a).localeCompare(String(b)));
    }

    const searchFieldsIdentical = this.compareSearchFields(searchFields, this.lastSearchFields);

    if (cacheInvalidated || !searchFieldsIdentical || searchTerm !== this.lastSearchTerm) {
      cacheInvalidated = true;

      this.lastSearchFields = searchFields;
      this.lastSearchTerm = searchTerm;
      this.filteredData = this.applyFilter(this.sortedData, searchFields, searchTerm);
    }

    if (cacheInvalidated || pageNumber !== this.lastPageNumber || pageSize !== this.lastPageSize) {
      this.pagedData = this.applyPage(this.filteredData, pageNumber, pageSize);
    }

    return {
      totalElements: this.filteredData.length,
      pageNumber,
      pageSize,
      data: this.pagedData
    };
  }

  private applySort(data: T[], sortField: string, sortAscending: boolean): T[] {

    const targetData = Array.from(data);
    const sortOrder = sortAscending ? 1 : -1;

    targetData.sort((a, b) => {
      return this.compare(a[sortField], b[sortField], this.collator) * sortOrder;
    });

    return targetData;
  }

  private compare(a: T, b: T, collator: Intl.Collator): number {
    // if a is null and b isn't
    if (a === null && b !== null) {
      // simply say that a is larger, and leave it at that
      return -1;
    }

    // if b is null and a isn't
    if (a !== null && b === null) {
      return 1;
      // if neither or both are null
    }

    // if data being compared are strings, we want to ignore case
    if (typeof a === 'string' && typeof b === 'string') {
      return collator.compare(a, b);
    }

    if (a instanceof FormattedNumber && b instanceof FormattedNumber) {
      return a.numericValue - b.numericValue;
    }

    if (a instanceof IconViewModel && b instanceof IconViewModel) {
      return a.order - b.order;
    }

    if (a > b) {
      return 1;
    } else if (a < b) {
      return -1;
    } else {
      return 0;
    }
  }

  private applyFilter(data: T[], searchColumns: Keys<T>, search: string): T[] {
    return this.filterService.filter(data, searchColumns, search);
  }

  private applyPage(data: T[], pageNumber: number, pageSize: number): T[] {
    const uncheckedStart = (pageNumber - 1) * pageSize;
    const uncheckedEnd = uncheckedStart + pageSize;

    const start = Math.min(data.length - 1, Math.max(0, uncheckedStart));
    const end = Math.min(data.length, Math.max(1, uncheckedEnd));

    return data.slice(start, end);
  }

  private compareSearchFields(a: Keys<T>, b: Keys<T>): boolean {
    if (!a && !b) {
      return true;
    }
    if (!a || !b) {
      return false;
    }
    if (a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; ++i) {
      if (a[i] !== b[i]) {
        return false;
      }
    }
    return true;
  }
}
